import DynamicToast from '@c/toasts/DynamicToast';
import { useMutation } from '@tanstack/react-query';
import Button from '@ui/Button';
import { FirebaseCallable, functions, httpsCallable } from '@util/firebase';
import {
  addPUItem,
  addWPSItem,
  ebaySyncOne,
  retryCategorization,
} from '@util/firestore/integrations/integrations.service';
import {
  bumpProduct,
  deleteListing,
  updateProductByKey,
} from '@util/firestore/products';
import { useDataDepotProduct } from '@util/hooks/useDataDepotProduct';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { ProductDocument } from 'models/product';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

const ProductAdminControls = ({ product }: { product: ProductDocument }) => {
  const { userDoc } = useAuth();
  const { showSuccessToast, showErrorToast, showToast } = useToastContext();
  const [isFeatured, setIsFeatured] = useState<boolean | undefined>(
    product.is_featured
  );
  const [loadingButton, setLoadingButton] = useState('');
  const router = useRouter();
  const adjustFeeMutation = useMutation({
    mutationFn: (fee: number) => {
      return httpsCallable<
        {
          fee: number;
          product_id: string;
          order_id?: string;
        },
        void
      >(
        functions,
        'v2_adjustFee'
      )({
        fee,
        product_id: product.id,
      });
    },
    onSuccess: () => {
      showSuccessToast('Fee adjusted successfully');
      window.location.reload();
    },
    onError: (e) => {
      showErrorToast(e.message);
    },
  });

  const setPopularityMutation = useMutation({
    mutationFn: async (popularity: number) => {
      return httpsCallable<{ popularity: number; product_id: string }, void>(
        functions,
        FirebaseCallable.setPopularity
      )({
        popularity,
        product_id: product.id,
      });
    },
    onSuccess: () => {
      showSuccessToast('Popularity set successfully');
      window.location.reload();
    },
    onError: (e) => {
      showErrorToast(e.message);
    },
  });

  const { dataDepotProduct } = useDataDepotProduct([
    ...(product.mpn ?? []),
    ...(product.sku ? [product.sku] : []),
  ]);

  const presentToast = (text: string) => {
    showToast(<DynamicToast text={text} />, {
      position: 'bottom-center',
      style: {
        borderRadius: '16px',
        backgroundColor: '#F9E5E5',
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: '#EF9021',
        width: 'min-content',
      },
    });
  };

  return (
    <div className="flex flex-wrap justify-center gap-x-5  ">
      <Button
        height="small"
        text="Adjust Fee"
        type="tertiary"
        width="small"
        loading={adjustFeeMutation.isPending}
        onClick={() => {
          const fee = prompt(
            'Enter the new fee as a percentage (min 0, max 10)'
          );
          if (!fee) return;
          const numFee = parseInt(fee);
          if (isNaN(numFee) || numFee < 0 || numFee > 10) {
            alert('Invalid fee');
          } else {
            adjustFeeMutation.mutate(numFee / 100);
          }
        }}
      />
      <Button
        height="small"
        text="Bump"
        type="tertiary"
        width="small"
        onClick={() =>
          bumpProduct(product.id).then(() =>
            presentToast('Product successfully bumped')
          )
        }
      />
      <Button
        height="small"
        text="Set Popularity"
        type="tertiary"
        width="small"
        loading={setPopularityMutation.isPending}
        onClick={() => {
          const popularityStr = prompt('Enter the new popularity value');
          if (!popularityStr) return;
          const numPop = parseInt(popularityStr);
          if (isNaN(numPop)) {
            alert('Invalid popularity');
          } else {
            setPopularityMutation.mutate(numPop);
          }
        }}
      />
      {isFeatured ? (
        <Button
          height="small"
          text="Unfeature"
          type="tertiary"
          width="small"
          onClick={() =>
            updateProductByKey(product.id, 'is_featured', false).then(() => {
              presentToast('Product successfully unfeatured');
              setIsFeatured(false);
            })
          }
        />
      ) : product.video || product.category === 'Dirt Bikes' ? (
        <Button
          height="small"
          text="Feature"
          type="tertiary"
          width="small"
          onClick={() =>
            updateProductByKey(product.id, 'is_featured', true).then(() => {
              presentToast('Product successfully featured');
              setIsFeatured(true);
            })
          }
        />
      ) : null}
      <Button
        text="Backend"
        height="small"
        type="tertiary"
        width="small"
        href={`https://console.firebase.google.com/project/${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}/firestore/data/~2Fproducts~2F${product.id}`}
        target="_blank"
      />
      <Button
        text="Edit Listing"
        height="small"
        width={'small'}
        type="tertiary"
        href={`/edit-listing/${product.id}`}
      />

      <Button
        type="tertiary"
        text="Delete Listing"
        width="small"
        height="small"
        disabled={product.out_of_stock && !userDoc?.roles?.support}
        onClick={() => {
          if (confirm('Are you sure you want to delete this listing?')) {
            deleteListing(product.id).then(() => {
              if (!userDoc?.roles?.support) router.push('/dashboard/listings');
              else window.location.reload();
            });
          }
        }}
      />
      {product.integration_info && (
        <Button
          type="green"
          loading={loadingButton === 'retryCategorization'}
          text="Retry Categorization (Beta)"
          onClick={async () => {
            if (confirm('Are you sure you want to retry categorization?')) {
              try {
                setLoadingButton('retryCategorization');
                await retryCategorization(product.id);
                alert('Categorization successfully retried');
                window.location.reload();
              } catch (e) {
                alert(e);
              } finally {
                setLoadingButton('');
              }
            }
          }}
        />
      )}
      {product.integration_info?.type === 'ebay' && (
        <>
          <Button
            href={`https://www.ebay.com/itm/${product.integration_info.remote_id}`}
            text="View on eBay"
            target="_blank"
          />
          <Button
            onClick={async () => {
              if (product.integration_info?.remote_id) {
                await ebaySyncOne({
                  remote_id: product.integration_info?.remote_id,
                  uid: product.seller_id,
                });
                window.location.reload();
              } else {
                alert('No remote ID found');
              }
            }}
            text="Resync from eBay"
            target="_blank"
          />
        </>
      )}
      {product.integration_info?.type === 'parts_unlimited' && (
        <>
          <Button
            // href={`https://www.ebay.com/itm/${product.integration_info.remote_id}`}
            href={`https://www.parts-unlimited.com/search;q=${
              product.has_variations
                ? product.variations?.[0].sku!
                : product.sku
            }`}
            text="View on Parts Unlimited"
            target="_blank"
          />
          <Button
            loading={loadingButton === 'addPUItem'}
            onClick={async () => {
              try {
                if (product.sku) {
                  setLoadingButton('addPUItem');
                  const res = await addPUItem({
                    sku: product.has_variations
                      ? product.variations?.[0].sku!
                      : product.sku,
                  });
                  if (!res.slug) {
                    alert('Failed to resync from Parts Unlimited');
                  } else {
                    window.location.reload();
                  }
                } else {
                  alert('No SKU found');
                }
              } catch (e) {
                alert(e);
              } finally {
                setLoadingButton('');
              }
            }}
            text="Resync from Parts Unlimited"
            target="_blank"
          />
        </>
      )}
      {product.integration_info?.type === 'wps' && (
        <>
          <Button
            href={`https://www.wpsorders.com/wpsonline/o2POPOUT.pgm?ITEM=${product.mpn?.[1]}`}
            text="View on WPS"
            target="_blank"
          />
          <Button
            loading={loadingButton === 'addWPSItem'}
            onClick={async () => {
              try {
                if (product.mpn?.[1]) {
                  setLoadingButton('addWPSItem');
                  const res = await addWPSItem({
                    sku: product.mpn[1],
                    product_id: product.id,
                  });
                  if (!res.url) {
                    alert('Failed to resync from WPS');
                  } else {
                    window.location.reload();
                  }
                } else {
                  alert('No SKU found');
                }
              } catch (e) {
                alert(e);
              } finally {
                setLoadingButton('');
              }
            }}
            text="Resync from WPS"
            target="_blank"
          />
        </>
      )}
      <Button
        text={dataDepotProduct ? 'Edit in Data Depot' : 'Add To Data Depot'}
        type="secondary"
        href={
          dataDepotProduct
            ? `/data-depot/${dataDepotProduct.id}`
            : `/data-depot?id=${product.id}&step=3`
        }
      />
    </div>
  );
};

export default ProductAdminControls;
